import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import NewSidebar from "../newSideBar";
import Navbar from "../NavBar";
import { Spinner } from "react-bootstrap";

const ChatContact = () => {
  const [contactData, setContactData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const contactsPerPage = 10;
  const localURL = localStorage.getItem("shopURL");
  const [chatLoading, setChatLoading] = useState(true);

  const headers = {
    "Content-Type": "application/json",
    "x-shop-url": localURL,
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_CHAT_BACKEND_URL}/user-detail`,
          { headers }
        );
        setContactData(response.data.users);
      } catch (err) {
        console.error("Error getting contact:", err);
      } finally {
        setChatLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
    setCurrentPage(1); // Reset to first page when sorting changes
  };

  const sortedData = useMemo(() => {
    if (!sortConfig.key) return contactData;

    return [...contactData].sort((a, b) => {
      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [contactData, sortConfig]);

  const filteredData = sortedData.filter(contact => 
    Object.values(contact).some(
      value => 
        value && value.toString().toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Pagination logic
  const indexOfLastContact = currentPage * contactsPerPage;
  const indexOfFirstContact = indexOfLastContact - contactsPerPage;
  const currentContacts = filteredData.slice(indexOfFirstContact, indexOfLastContact);
  const totalPages = Math.ceil(filteredData.length / contactsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  
  // Generate page numbers with ellipsis for large page ranges
  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      const leftBound = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
      const rightBound = Math.min(totalPages, leftBound + maxVisiblePages - 1);
      
      if (leftBound > 1) {
        pageNumbers.push(1);
        if (leftBound > 2) {
          pageNumbers.push('...');
        }
      }
      
      for (let i = leftBound; i <= rightBound; i++) {
        pageNumbers.push(i);
      }
      
      if (rightBound < totalPages) {
        if (rightBound < totalPages - 1) {
          pageNumbers.push('...');
        }
        pageNumbers.push(totalPages);
      }
    }
    
    return pageNumbers;
  };

  return (
    <div className="contacts-dashboard">
      <Navbar />
      <div className="contacts-layout">
        <NewSidebar />
        <div className="contacts-main-content">
          {chatLoading ? (
            <div className="contacts-loading-overlay">
              <Spinner animation="border" variant="primary" className="contacts-spinner" />
            </div>
          ) : (
            <div className="contacts-container">
              <div className="contacts-header">
                <h2 className="contacts-title">User Contacts</h2>
                <div className="contacts-search">
                  <input
                    type="text"
                    placeholder="Search contacts..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="search-input"
                  />
                  <i className="mdi mdi-magnify search-icon"></i>
                </div>
              </div>

              <div className="contacts-table-container">
                <table className="contacts-table">
                  <thead>
                    <tr>
                      <th onClick={() => handleSort('name')}>
                        Name
                        {sortConfig.key === 'name' && (
                          <i className={`mdi mdi-chevron-${sortConfig.direction === 'asc' ? 'up' : 'down'}`}></i>
                        )}
                      </th>
                      <th onClick={() => handleSort('email')}>
                        Email
                        {sortConfig.key === 'email' && (
                          <i className={`mdi mdi-chevron-${sortConfig.direction === 'asc' ? 'up' : 'down'}`}></i>
                        )}
                      </th>
                      <th onClick={() => handleSort('location')}>
                        Location
                        {sortConfig.key === 'location' && (
                          <i className={`mdi mdi-chevron-${sortConfig.direction === 'asc' ? 'up' : 'down'}`}></i>
                        )}
                      </th>
                      <th onClick={() => handleSort('system')}>
                        System
                        {sortConfig.key === 'system' && (
                          <i className={`mdi mdi-chevron-${sortConfig.direction === 'asc' ? 'up' : 'down'}`}></i>
                        )}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentContacts.length > 0 ? (
                      currentContacts.map((contact, index) => (
                        <tr key={index}>
                          <td>
                            <div className="contact-name">
                              <span className="contact-avatar">
                                {contact.name?.charAt(0).toUpperCase()}
                              </span>
                              {contact.name}
                            </div>
                          </td>
                          <td>{contact.email}</td>
                          <td>{contact.location}</td>
                          <td>
                            <span className="system-badge">{contact.system}</span>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="4" className="no-results">
                          <div className="empty-contacts">
                            <i className="mdi mdi-account-search"></i>
                            <p>No contacts found matching your search</p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>

                {/* Pagination */}
                {filteredData.length > contactsPerPage && (
                  <div className="contacts-pagination">
                    <button
                      onClick={() => paginate(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="pagination-arrow"
                    >
                      <i className="mdi mdi-chevron-left"></i>
                    </button>

                    {getPageNumbers().map((number, index) => (
                      number === '...' ? (
                        <span key={index} className="pagination-ellipsis">...</span>
                      ) : (
                        <button
                          key={index}
                          onClick={() => paginate(number)}
                          className={`pagination-number ${currentPage === number ? 'active' : ''}`}
                        >
                          {number}
                        </button>
                      )
                    ))}

                    <button
                      onClick={() => paginate(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className="pagination-arrow"
                    >
                      <i className="mdi mdi-chevron-right"></i>
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatContact;